import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { CoilSpring } from '@/assets/icons/CoilSpring';
import { useMeasureOptions } from '@/hooks/store/use-measure-options-store';
import { useTranslation } from '@/hooks/use-translation';
import type { KeyboardLike, KeyWith } from '@/lib/piano/keyboard';
import type { TouchWeightKeyAnalysis } from '@/lib/piano/touch-design/touch-weight-key-analysis';
import { CHART_HEIGHTS } from './chart-geometry';
import { scaleOver } from './echarts/build-base-option';
import { EmptyState } from '@/components/app-ui/EmptyState';
import { ChartEmptyFrame } from './figure/ChartEmptyFrame';
import { ChartFigure } from './figure/ChartFigure';
import { ChartKeyboardAxis } from './figure/ChartKeyboardAxis';
import { ChartLegend, type ChartLegendItem } from './figure/ChartLegend';
import { useChartFigure } from './hooks/use-chart-figure';
import { useGenerateSerie } from './hooks/use-generate-serie';
import { KeyboardChart, TouchDesignSerieVariant } from './KeyboardChart';
import { serieValues, type ChartReadingContext, type KeyRangeNote, type TouchDesignSerie } from './interfaces';
import { legendIdOf, useLegendToggles } from './hooks/use-legend-toggles';

/**
 * The scales of the mockups: what Analyze reads is drawn from 0 to 30 g, ruled
 * every 10 g; what Design sets, from 0 to 12 g, ruled every 3 g.
 */
const SPRING_WEIGHT_SCALES = {
  analyze: {
    interval: 10,
    max: 30,
    min: 0,
    unit: 'g'
  },
  // A designed spring stays under a dozen grams: against 30 g its curve would
  // keep to the bottom third of the plot.
  design: {
    interval: 3,
    max: 12,
    min: 0,
    unit: 'g'
  }
};
type KeyWithSupportSpringBalanceWeight<T> = T & Pick<TouchWeightKeyAnalysis, 'supportSpringBalanceWeight'>;
export interface SprungKeys {
  count: number;
  first: number;
  last: number;
}
export interface SupportSpringBalanceWeightChartProps<T> {
  /**
   * The buttons of the empty state. Passing them is what turns an empty chart
   * into the "nothing measured yet" panel: the page owns the buttons because
   * it knows where they lead, and Design, which shares this chart, has no such
   * state to offer.
   */
  emptyStateActions?: React.ReactNode;
  /** Legend entries contributed by the page, e.g. the design target. */
  extraLegendItems?: ChartLegendItem[];
  keyboard: KeyboardLike<KeyWith<KeyWithSupportSpringBalanceWeight<T>>>;
  /**
   * The reading strip at the head of the figure. It is a function so that the
   * strip reads the very series the chart draws, rather than computing them
   * again.
   */
  renderReading?: (context: ChartReadingContext) => React.ReactNode;
  /**
   * The keys the designed springs are on, named on the plot and washed on
   * its keyboard, as the mockup does: where the target stops is the design,
   * not a gap in it. `null` is a design that fits none, said across the
   * whole keyboard; left out, the chart is not drawing a design at all.
   */
  sprungKeys?: SprungKeys | null;
  targetSerie?: TouchDesignSerie;
  variant?: 'analyze' | 'design';
}
export const SupportSpringBalanceWeightChart = t0 => {
  const $ = _c(99);
  const {
    emptyStateActions,
    extraLegendItems,
    keyboard,
    renderReading,
    sprungKeys,
    targetSerie,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "analyze" : t1;
  const {
    t
  } = useTranslation();
  const {
    geometry,
    hover,
    keys
  } = useChartFigure(keyboard);
  const {
    isSerieEmpty,
    generateSerie
  } = useGenerateSerie(keyboard);
  const {
    useSupportSpringMeasurements
  } = useMeasureOptions();
  let t2;
  if ($[0] !== useSupportSpringMeasurements) {
    t2 = key => useSupportSpringMeasurements ? key.payload.supportSpringBalanceWeight : null;
    $[0] = useSupportSpringMeasurements;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== generateSerie || $[3] !== t || $[4] !== t2) {
    t3 = generateSerie(t2, t("Measured spring-supported weight"), TouchDesignSerieVariant.Measured);
    $[2] = generateSerie;
    $[3] = t;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const measuredSerie = t3;
  let t4;
  if ($[6] !== isSerieEmpty || $[7] !== measuredSerie) {
    t4 = isSerieEmpty(measuredSerie);
    $[6] = isSerieEmpty;
    $[7] = measuredSerie;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const isEmpty = t4;
  let t5;
  if ($[9] !== targetSerie) {
    t5 = targetSerie ? [{
      ...targetSerie,
      isFilled: true
    }] : [];
    $[9] = targetSerie;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== isEmpty || $[12] !== measuredSerie) {
    t6 = isEmpty ? [] : [measuredSerie];
    $[11] = isEmpty;
    $[12] = measuredSerie;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = [...t5, ...t6];
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const series = t7;
  const lastKey = keys[keys.length - 1]?.number;
  const firstKey = keys[0]?.number;
  let t8;
  bb0: {
    if (sprungKeys === undefined) {
      t8 = undefined;
      break bb0;
    }
    if (sprungKeys === null) {
      let t9;
      if ($[17] !== firstKey || $[18] !== lastKey || $[19] !== t) {
        t9 = firstKey === undefined || lastKey === undefined ? undefined : [{
          from: firstKey,
          kind: "label",
          text: t("without springs"),
          to: lastKey
        }];
        $[17] = firstKey;
        $[18] = lastKey;
        $[19] = t;
        $[20] = t9;
      } else {
        t9 = $[20];
      }
      t8 = t9;
      break bb0;
    }
    const {
      count,
      first,
      last
    } = sprungKeys;
    let t9;
    if ($[21] !== count || $[22] !== t) {
      t9 = t("{{count}} springs", {
        count
      });
      $[21] = count;
      $[22] = t;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    let t10;
    if ($[24] !== first || $[25] !== last || $[26] !== t9) {
      t10 = {
        from: first,
        kind: "bracket",
        text: t9,
        to: last
      };
      $[24] = first;
      $[25] = last;
      $[26] = t9;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t11;
    if ($[28] !== last || $[29] !== lastKey || $[30] !== t) {
      t11 = lastKey !== undefined && last < lastKey ? [{
        from: last + 1,
        kind: "label" as const,
        text: t("without springs"),
        to: lastKey
      }] : [];
      $[28] = last;
      $[29] = lastKey;
      $[30] = t;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    let t12;
    if ($[32] !== t10 || $[33] !== t11) {
      t12 = [t10, ...t11];
      $[32] = t10;
      $[33] = t11;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    t8 = t12;
  }
  const rangeNotes = t8;
  let t9;
  if ($[35] !== keys || $[36] !== sprungKeys) {
    t9 = sprungKeys ? new Set(keys.map(_temp).filter(number => number >= sprungKeys.first && number <= sprungKeys.last)) : undefined;
    $[35] = keys;
    $[36] = sprungKeys;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  const markedKeys = t9;
  const t10 = SPRING_WEIGHT_SCALES[variant];
  let t11;
  if ($[38] !== series || $[39] !== t10) {
    t11 = scaleOver(t10, serieValues(series));
    $[38] = series;
    $[39] = t10;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  const scale = t11;
  const {
    isShown,
    withToggles
  } = useLegendToggles();
  let t12;
  if ($[41] !== isShown || $[42] !== series) {
    t12 = series.filter(serie => isShown(legendIdOf(serie)));
    $[41] = isShown;
    $[42] = series;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  const drawnSeries = t12;
  let t13;
  if ($[44] !== extraLegendItems || $[45] !== isEmpty || $[46] !== t || $[47] !== withToggles) {
    t13 = withToggles([...(extraLegendItems ?? []), ...(isEmpty ? [] : [{
      colorClassName: "text-foreground",
      id: "measured",
      label: t("Measured spring-supported weight"),
      mark: "dot" as const
    }])]);
    $[44] = extraLegendItems;
    $[45] = isEmpty;
    $[46] = t;
    $[47] = withToggles;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  const legendItems = t13;
  const plotHeight = variant === "analyze" ? CHART_HEIGHTS.plot : CHART_HEIGHTS.plotCompact;
  if (isEmpty && emptyStateActions) {
    const t14 = SPRING_WEIGHT_SCALES[variant];
    let t15;
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <CoilSpring />;
      $[49] = t15;
    } else {
      t15 = $[49];
    }
    let t16;
    if ($[50] !== t) {
      t16 = t("No wippen support spring measurement");
      $[50] = t;
      $[51] = t16;
    } else {
      t16 = $[51];
    }
    let t17;
    if ($[52] !== t) {
      t17 = t("Enable the \u201CMeasure Wippen Support Spring tension\u201D option, then record the down weight with the spring attached in Measure.");
      $[52] = t;
      $[53] = t17;
    } else {
      t17 = $[53];
    }
    let t18;
    if ($[54] !== t) {
      t18 = t("This measurement is optional: you can design your springs without it.");
      $[54] = t;
      $[55] = t18;
    } else {
      t18 = $[55];
    }
    let t19;
    if ($[56] !== emptyStateActions || $[57] !== t16 || $[58] !== t17 || $[59] !== t18) {
      t19 = <EmptyState icon={t15} title={t16} text={t17} note={t18} actions={emptyStateActions} />;
      $[56] = emptyStateActions;
      $[57] = t16;
      $[58] = t17;
      $[59] = t18;
      $[60] = t19;
    } else {
      t19 = $[60];
    }
    let t20;
    if ($[61] !== geometry || $[62] !== keys || $[63] !== plotHeight || $[64] !== t14 || $[65] !== t19 || $[66] !== variant) {
      t20 = <ChartEmptyFrame geometry={geometry} height={plotHeight} keys={keys} scale={t14} variant={variant}>{t19}</ChartEmptyFrame>;
      $[61] = geometry;
      $[62] = keys;
      $[63] = plotHeight;
      $[64] = t14;
      $[65] = t19;
      $[66] = variant;
      $[67] = t20;
    } else {
      t20 = $[67];
    }
    return t20;
  }
  let t14;
  if ($[68] !== hover.hoveredKey || $[69] !== measuredSerie || $[70] !== renderReading) {
    t14 = renderReading?.({
      hoveredKey: hover.hoveredKey,
      measuredSerie,
      referenceSeries: []
    });
    $[68] = hover.hoveredKey;
    $[69] = measuredSerie;
    $[70] = renderReading;
    $[71] = t14;
  } else {
    t14 = $[71];
  }
  const t15 = hover.hoveredKey;
  let t16;
  if ($[72] !== legendItems) {
    t16 = legendItems.length > 0 ? <ChartLegend items={legendItems} /> : undefined;
    $[72] = legendItems;
    $[73] = t16;
  } else {
    t16 = $[73];
  }
  const t17 = hover.hoveredKey;
  const t18 = hover.setHoveredKey;
  let t19;
  if ($[74] !== t) {
    t19 = t("Keyboard: hover a key to read its measurement");
    $[74] = t;
    $[75] = t19;
  } else {
    t19 = $[75];
  }
  let t20;
  if ($[76] !== hover.hoveredKey || $[77] !== hover.setHoveredKey || $[78] !== keys || $[79] !== markedKeys || $[80] !== t19) {
    t20 = <ChartKeyboardAxis keys={keys} highlightedKey={t17} markedKeys={markedKeys} onKeyHover={t18} description={t19} />;
    $[76] = hover.hoveredKey;
    $[77] = hover.setHoveredKey;
    $[78] = keys;
    $[79] = markedKeys;
    $[80] = t19;
    $[81] = t20;
  } else {
    t20 = $[81];
  }
  let t21;
  if ($[82] !== drawnSeries || $[83] !== geometry || $[84] !== hover.bindChart || $[85] !== plotHeight || $[86] !== rangeNotes || $[87] !== scale || $[88] !== variant) {
    t21 = <KeyboardChart variant={variant} geometry={geometry} height={plotHeight} onChartReady={hover.bindChart} rangeNotes={rangeNotes} scale={scale} series={drawnSeries} />;
    $[82] = drawnSeries;
    $[83] = geometry;
    $[84] = hover.bindChart;
    $[85] = plotHeight;
    $[86] = rangeNotes;
    $[87] = scale;
    $[88] = variant;
    $[89] = t21;
  } else {
    t21 = $[89];
  }
  let t22;
  if ($[90] !== geometry || $[91] !== hover.figureProps || $[92] !== hover.hoveredKey || $[93] !== t14 || $[94] !== t16 || $[95] !== t20 || $[96] !== t21 || $[97] !== variant) {
    t22 = <ChartFigure reading={t14} variant={variant} geometry={geometry} highlightedKey={t15} header={t16} keyboard={t20} {...hover.figureProps}>{t21}</ChartFigure>;
    $[90] = geometry;
    $[91] = hover.figureProps;
    $[92] = hover.hoveredKey;
    $[93] = t14;
    $[94] = t16;
    $[95] = t20;
    $[96] = t21;
    $[97] = variant;
    $[98] = t22;
  } else {
    t22 = $[98];
  }
  const figure = t22;
  return figure;
};
function _temp(key_0) {
  return key_0.number;
}